// @flow
import React, { useContext } from 'react';
import { Formik } from 'formik';

import { ClusterContext } from '../../../contexts/ClusterContext';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { MonthlyInputsMetaDataContext } from '../../../contexts/MonthlyInputsMetaDataContext';
import { CLUSTER_TOP_LEVEL_CONFIG } from '../../../configuration/cluster-top-level-config';
import { getFormikFormInputsFromColConfigAndInputSource } from '../../../common-utils/utils';
import { StyledContainer } from '../../../common-utils/styledComponents';
import { PromptIfFormHasUnSavedChanges } from '../../presentational/PromptIfFormHasUnSavedChanges';
import { Form } from '../../../formik-utils/formikSUIWrapper';
import {
  CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME,
  CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_RETENTION_BACKEND_NAME,
  CLUSTER_RETENTION_INFINITE_BACKEND_NAME,
  CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG,
  TABLEFLOW_VERSIONS,
  IS_RTCE_LIVE_IN_CE1,
  TCO_INPUTS_JSON_BACKEND_NAME,
} from '../../../constants';
import { ClusterEnabledContainer } from '../cluster-details-inputs/ClusterEnabledContainer';
import { Spacer } from '../../presentational/spacing/Spacer';
import FormListener from '../../../formik-utils/FormListener';
import { ValidateFormOnMount } from '../../../formik-utils/ValidateFormOnMount';
import { getTableflowVersion } from '../../../custom-hooks/useTableflowVersion';
import { getTableflowInputsFromCluster } from '../cluster-monthly-inputs/tableflow-config';

import { ClusterAccordionsContainer } from './ClusterAccordionsContainer';
import { ClusterDeleteContainer } from './ClusterDeleteContainer';

export const ClusterTopLevelContainerForm = () => {
  const estimate = useContext(EstimateContext);
  const cluster = useContext(ClusterContext);
  const metaData = useContext(MonthlyInputsMetaDataContext);
  const retentionInputs = cluster.inputs.retention;
  const tableflowVersion = getTableflowVersion(estimate);
  const clusterInputsSource = {
    ...cluster.inputs,
    [CLUSTER_RETENTION_INFINITE_BACKEND_NAME]: !retentionInputs.is_finite_retention,
    [CLUSTER_RETENTION_BACKEND_NAME]: retentionInputs[CLUSTER_RETENTION_BACKEND_NAME],
    id: cluster.inputs.id,
    name: cluster.cluster_name,
    isEnabled: cluster.is_enabled,
    calculations: { inputs: cluster.calculations },
    [CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG]: cluster[CLUSTER_KAFKA_MONTHLY_INPUTS_JSON_CONFIG],
    [CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG]: cluster[CLUSTER_KSQLDB_MONTHLY_INPUTS_JSON_CONFIG],
    [CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG]: {
      connectors: cluster?.[CLUSTER_CONNECTORS_MONTHLY_INPUTS_JSON_CONFIG],
    },
    [CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG]:
      cluster[CLUSTER_CLUSTER_LINKING_MONTHLY_INPUTS_JSON_CONFIG],
    [CLUSTER_AVAILABLE_CONNECTORS_LIST_BACKEND_NAME]: 'activemqsource',
    // todo:: Remove the hardcoding of ActiveMQSource
    // storage_type must be nested within the monthly inputs object structure
    // This ensures:
    // 1. SelectField can access values.tableflow.storage_type
    // 2. ClusterInputTable monitors changes in the entire object (including storage_type)
    // 3. Save/Reset buttons react to both monthly inputs AND storage_type changes
    // 4. tableflowMonthlyDataInputTransformationFunc can preserve storage_type during data transformation
    // Each version has its own component and its own Formik key; only the active one is seeded
    // here -- the other's config entry transforms an absent value (see
    // clusterMonthlyDataInputTransformationFunc's undefined guard).
    ...(tableflowVersion !== TABLEFLOW_VERSIONS.V2 && {
      [CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG]: {
        ...getTableflowInputsFromCluster(cluster, TABLEFLOW_VERSIONS.LEGACY),
      },
    }),
    ...(tableflowVersion === TABLEFLOW_VERSIONS.V2 && {
      [CLUSTER_TABLEFLOW_V2_MONTHLY_INPUTS_JSON_CONFIG]: {
        ...getTableflowInputsFromCluster(cluster, TABLEFLOW_VERSIONS.V2),
      },
    }),
    ...(IS_RTCE_LIVE_IN_CE1 && {
      [CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG]: cluster[CLUSTER_RTCE_MONTHLY_INPUTS_JSON_CONFIG],
    }),
    [TCO_INPUTS_JSON_BACKEND_NAME]: cluster?.[TCO_INPUTS_JSON_BACKEND_NAME] ?? {},
  };

  const { initialValues, initialTouched, validationSchema } =
    getFormikFormInputsFromColConfigAndInputSource(CLUSTER_TOP_LEVEL_CONFIG, clusterInputsSource, {
      estimate,
      cluster,
      metaData,
    });

  return (
    <StyledContainer>
      <Formik
        enableReinitialize={true}
        initialTouched={initialTouched}
        initialValues={initialValues}
        onSubmit={() => {}}
        validateOnBlur={true}
        validateOnChange={false}
        validateOnMount={true}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form autoComplete="off">
            <ValidateFormOnMount />
            <FormListener formik={formik} />
            <PromptIfFormHasUnSavedChanges />
            <ClusterEnabledContainer />
            <ClusterDeleteContainer />
            <Spacer y={10} />
            <ClusterAccordionsContainer />
          </Form>
        )}
      </Formik>
    </StyledContainer>
  );
};
